import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import classNames from "classnames";

import styles from "./style.module.scss";

import Tip from "ui/Tip";
import Button from "ui/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/pro-regular-svg-icons";

import {
  CellData,
  CoreProps,
  getClassName,
  getHeaderClassName,
  reverse,
  SortOrder,
} from "./util";
import { useGridContext } from "./GridContext";
import { formatBytes, formatMs, formatNumber } from "utils/format";
import { useDeepEqualMemo } from "utils/hooks";

export type ImplSortProps<T> = {
  handleSort: (field: keyof T, order: SortOrder, col: number) => void;
  sortedOrder?: SortOrder;
} & (
  | {
      sortedBy?: keyof T;
      sortedByIdx?: never;
    }
  | {
      sortedByIdx?: number;
      sortedBy?: never;
    }
);

export type ImplProps<T> = CoreProps<T> & ImplSortProps<T>;

function GridImpl<T>({
  className,
  style,
  data,
  noRowsText,
  columns,
  striped,
  pageSize = Infinity,
  cellRenderer,
  ...sortProps
}: ImplProps<T>) {
  const sortedColIdx =
    sortProps.sortedByIdx ??
    columns.findIndex((col) => col.field == sortProps.sortedBy);
  const { hiddenFields, savedColumnWidths, setColumnWidths } = useGridContext();
  const visibleColumns = columns
    .map((col, colIdx) => ({ col, colIdx }))
    .filter(({ col }) => col.width !== false && !hiddenFields.has(col.field));

  const [page, setPage] = useState(0);
  const gridData = useDeepEqualMemo(data);

  useEffect(() => {
    setPage(0);
  }, [gridData]);

  const widths = visibleColumns.map(({ col }) => col.width) as string[];
  // Columns with an empty header (icon/status indicators) never resize: they
  // keep their original width and get no resize handle. We hold them fixed
  // during any neighbor's drag by giving them zero redistribution weight.
  const fixedColumns = visibleColumns.map(({ col }) => col.header === "");

  // Session-only, user-dragged column track sizes (see startResize). Reset when
  // the visible-column set changes so a stale override can't outlive its tracks.
  const gridRef = useRef<HTMLDivElement>(null);
  const [trackOverride, setTrackOverride] = useState<string[] | null>(null);
  // Index of the column boundary currently being dragged (null when not
  // resizing); drives the drag overlay and keeps that divider highlighted while
  // the overlay covers the grip and suppresses its :hover.
  const [resizingBoundary, setResizingBoundary] = useState<number | null>(null);
  const visibleKey = visibleColumns.map(({ col }) => col.field).join(",");
  useEffect(() => {
    setTrackOverride(null);
  }, [visibleKey]);

  // Detaches the in-flight drag's document listeners (set in startResize, null
  // when no drag is active). Lets us also tear them down if the grid unmounts
  // mid-drag, when onUp would otherwise never fire.
  const detachDragRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    return () => detachDragRef.current?.();
  }, []);

  // Begin a column resize from the boundary on the right edge of column
  // leftIdx. We snapshot the current resolved pixel width of every track and
  // redistribute a constant total as the pointer moves: dragging the boundary
  // widens the columns on one side and narrows those on the other by the same
  // amount, weighted by distance from the divider so the adjacent columns move
  // most and the effect falls off further away (see redistribute).
  // Each track is re-emitted keeping its kind: flexible tracks stay fr
  // (responsive), the rest are pinned to px. Because the fr pool resolves to
  // container - Σ(fixed px) = Σ(flex px), setting a flex track's fr weight to
  // its measured px keeps it exactly its current size now, so the px deltas are
  // consistent across track kinds.
  const startResize = (e: React.MouseEvent, leftIdx: number) => {
    e.preventDefault();
    e.stopPropagation();
    const grid = gridRef.current;
    if (!grid) {
      return;
    }
    const base = getComputedStyle(grid)
      .gridTemplateColumns.split(" ")
      .map((w) => parseFloat(w));
    const startX = e.clientX;
    // How much each side can give up before all of its resizable columns hit
    // the minimum width. Fixed columns don't shrink, so they add no capacity.
    // The drag is bounded by the shrinking side's capacity in each direction.
    let leftCapacity = 0;
    for (let i = 0; i <= leftIdx; i++) {
      if (!fixedColumns[i]) {
        leftCapacity += base[i] - RESIZE_WIDTH_MIN;
      }
    }
    let rightCapacity = 0;
    for (let i = leftIdx + 1; i < base.length; i++) {
      if (!fixedColumns[i]) {
        rightCapacity += base[i] - RESIZE_WIDTH_MIN;
      }
    }

    const emit = (px: number[]): string[] =>
      px.map((w, i) => (isFlexWidth(widths[i]) ? `${w}fr` : `${w}px`));

    // Last emitted tracks; persisted on mouse-up so the resize survives reloads.
    let latest: string[] | null = null;
    const onMove = (ev: MouseEvent) => {
      let delta = ev.clientX - startX;
      // Clamp so neither side is pushed past the point where all of its
      // resizable columns have collapsed to the minimum width.
      delta = Math.max(delta, -leftCapacity);
      delta = Math.min(delta, rightCapacity);
      latest = emit(redistribute(base, leftIdx, delta, fixedColumns));
      setTrackOverride(latest);
    };
    const detach = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      detachDragRef.current = null;
    };
    const onUp = () => {
      detach();
      setResizingBoundary(null);
      // Commit once, at the end of the drag (not on every move). trackOverride
      // stays set to this same template until the visible-column set changes,
      // so there's no flicker as the persisted value takes over.
      if (latest) {
        setColumnWidths(latest);
      }
    };
    // Track teardown so the unmount effect can also detach these document
    // listeners if the grid unmounts mid-drag (onUp would otherwise never fire).
    detachDragRef.current = detach;
    // Show a full-viewport overlay for the duration of the drag (see the JSX
    // below). It sits on top with a col-resize cursor, so the cursor stays
    // stable instead of flickering as the pointer passes over headers/cells
    // with their own cursors.
    setResizingBoundary(leftIdx);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const handleSortInternal = (e: React.MouseEvent<HTMLButtonElement>) => {
    const colIdx = parseInt(e.currentTarget.dataset.columnindex ?? "", 10);
    const newSortCol = columns[colIdx];
    if (!newSortCol || newSortCol.disableSort) {
      return;
    }
    setPage(0);
    const newSortOrder =
      colIdx === sortedColIdx && sortProps.sortedOrder
        ? reverse(sortProps.sortedOrder)
        : newSortCol.defaultSortOrder ?? "ASC";

    sortProps.handleSort(newSortCol.field, newSortOrder, colIdx);
  };

  const isPaginated = data.length > pageSize;
  const pageFirstItem = page * pageSize;
  const nextPageFirstItem = (page + 1) * pageSize;
  // N.B.: we need to slice in zero-indexed mode but show labels in one-indexed
  const pageFirstItemLabel = String(pageFirstItem + 1);
  const pageLastItemLabel = String(Math.min(nextPageFirstItem, data.length));
  const visibleData = isPaginated
    ? gridData.slice(pageFirstItem, nextPageFirstItem)
    : gridData.slice();

  const pagePrevDisabled = pageFirstItem === 0;
  const pageNextDisabled = nextPageFirstItem >= data.length;

  const handleShowPrev = () => {
    setPage((p) => p - 1);
  };
  const handleShowNext = () => {
    setPage((p) => p + 1);
  };

  if (visibleData.length === 0) {
    return (
      <div className={styles.cell}>{noRowsText ?? "No data available"}</div>
    );
  }

  // Precedence: the in-progress drag (trackOverride), then a remembered resize
  // (savedColumnWidths), then the column defaults. For the defaults: once the
  // user has hidden columns, the remaining tracks can sum to less than the
  // container, leaving empty space. fillTracks lets them stretch to fill it
  // (only when no track is already flexible).
  const templateTracks = trackOverride
    ? trackOverride
    : savedColumnWidths
    ? savedColumnWidths
    : hiddenFields.size > 0
    ? fillTracks(widths)
    : widths;
  const combinedStyles = {
    // Fixed (empty-header) columns always render at their declared width,
    // overriding any drag/persisted/fill value — they never resize, and this
    // also neutralizes stale saved widths from before they were excluded.
    gridTemplateColumns: templateTracks
      .map((track, i) => (fixedColumns[i] ? widths[i] : track))
      .join(" "),
    ...style,
  };

  return (
    <>
      {resizingBoundary !== null &&
        ReactDOM.createPortal(
          <div className={styles.resizeOverlay} aria-hidden="true" />,
          document.body,
        )}
      <HeightWrapper isPaginated={isPaginated} page={page}>
        <div
          ref={gridRef}
          className={classNames(styles.grid, className)}
          style={combinedStyles}
        >
          {visibleColumns.map(({ col, colIdx }, i) => {
            const headerClassName = getHeaderClassName(col);
            const isSortedByThis = colIdx === sortedColIdx;
            const colHeaderContent = (
              <>
                {col.header ?? col.field}
                {col.tip && (
                  <span className={styles.headingTip}>
                    {" "}
                    <Tip content={col.tip} />
                  </span>
                )}
              </>
            );
            const fullHeaderClassName = classNames(
              styles.headingCell,
              headerClassName,
            );
            // A single grip centered on the column boundary. It straddles the
            // boundary (overhanging into the next column) because the header
            // content's overflow is clipped by an inner wrapper (.headingContent)
            // rather than the cell itself, so the cell no longer clips the grip.
            // The grip lives inside the header (itself a sort <button>), so it
            // can't be a button, and resizing is a pointer affordance — hence
            // the a11y interaction rules are disabled here.
            //
            // No grip on a fixed (empty-header) column, and none unless there's
            // a resizable column to the right for the drag to redistribute
            // against.
            const hasResizableRight = fixedColumns
              .slice(i + 1)
              .some((isFixed) => !isFixed);
            const resizeHandle = !fixedColumns[i] && hasResizableRight && (
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
              <span
                className={classNames(
                  styles.resizeHandle,
                  resizingBoundary === i && styles.resizeHandleActive,
                )}
                onMouseDown={(e) => startResize(e, i)}
                // Headers are sort buttons; keep a click on the grip from
                // triggering a sort.
                onClick={(e) => e.stopPropagation()}
                role="separator"
                aria-orientation="vertical"
              />
            );
            if (col.disableSort) {
              return (
                <div
                  key={i}
                  data-field={col.field}
                  className={fullHeaderClassName}
                >
                  <span className={styles.headingContent}>
                    {colHeaderContent}
                  </span>
                  {resizeHandle}
                </div>
              );
            }
            return (
              <Button
                bare
                key={i}
                data-field={col.field}
                data-columnindex={colIdx}
                className={fullHeaderClassName}
                onClick={handleSortInternal}
              >
                <span className={styles.headingContent}>
                  {colHeaderContent}
                  {isSortedByThis &&
                    (sortProps.sortedOrder === "ASC" ? (
                      <SortCaretUp />
                    ) : (
                      <SortCaretDown />
                    ))}
                </span>
                {resizeHandle}
              </Button>
            );
          })}
          {visibleData.map((row, i) => {
            const isLastRow = i === visibleData.length - 1;
            return (
              <React.Fragment key={i}>
                {visibleColumns.map(({ col }, j) => {
                  const cellProps = {
                    field: col.field,
                    fieldData: row[col.field],
                    rowData: row,
                  };
                  const cellClassName = getClassName(
                    col,
                    cellProps.field,
                    cellProps.fieldData,
                    cellProps.rowData,
                  );
                  const CellContentRenderer =
                    (col.renderer as React.ComponentType<
                      CellData<T, keyof T>
                    >) ?? DefaultCellContentRenderer;
                  const title =
                    col.title === true
                      ? String(cellProps.fieldData)
                      : typeof col.title === "function"
                      ? col.title(cellProps)
                      : undefined;

                  const key = `${i}-${j}`;
                  const commonProps = {
                    title: title,
                    className: classNames(
                      styles.cell,
                      striped
                        ? i % 2 === 0 && styles.cellAlt
                        : !isLastRow && styles.cellBordered,
                      cellClassName,
                    ),
                    row: i,
                    column: j,
                  };
                  const content =
                    cellProps.fieldData == null &&
                    col.nullValue !== undefined ? (
                      col.nullValue
                    ) : (
                      <CellContentRenderer {...cellProps} />
                    );

                  if (cellRenderer) {
                    const CellRenderer = cellRenderer;
                    return (
                      <CellRenderer key={key} {...commonProps} {...cellProps}>
                        {content}
                      </CellRenderer>
                    );
                  } else {
                    return (
                      <div key={key} {...commonProps}>
                        {content}
                      </div>
                    );
                  }
                })}
              </React.Fragment>
            );
          })}
        </div>
      </HeightWrapper>
      {isPaginated && (
        <div className="flex pt-[8px] items-center justify-end">
          <span className="mr-[8px]">
            {pageFirstItemLabel}-{pageLastItemLabel} of {data.length}
          </span>
          <Button
            bare
            className="!text-[18px] !py-0 !px-[8px] disabled:text-[#999] disabled:cursor-not-allowed"
            disabled={pagePrevDisabled}
            onClick={handleShowPrev}
          >
            <FontAwesomeIcon icon={faAngleLeft} />
          </Button>
          <Button
            bare
            className="!text-[18px] !py-0 !px-[8px] disabled:text-[#999] disabled:cursor-not-allowed"
            disabled={pageNextDisabled}
            onClick={handleShowNext}
          >
            <FontAwesomeIcon icon={faAngleRight} />
          </Button>
        </div>
      )}
    </>
  );
}

// Whether a column width grows with the container, for the purpose of a user
// resize: tracks that contain fr/auto, or are a bare percentage. Such tracks
// are re-emitted as fr after a resize so they keep flexing; everything else
// (fixed px, or minmax with a fixed cap) is pinned to px. N.B. this is broader
// than fillTracks' notion, which counts only fr/auto since a bare percentage
// doesn't absorb leftover slack.
export function isFlexWidth(width: string): boolean {
  return (
    width.includes("fr") ||
    width.includes("auto") ||
    /^[\d.]+%$/.test(width.trim())
  );
}

// Minimum width (px) a column can be shrunk to by a resize drag.
export const RESIZE_WIDTH_MIN = 48;

// Each column one step further from the dragged divider moves this fraction as
// much as the one before it, so the change falls off with distance.
export const RESIZE_FALLOFF = 0.7;

// Grow the given columns of `out` by `amount` px total (amount > 0), split by
// the supplied per-column weights. Growth is uncapped — the caller keeps the
// overall total constant, so the group only reclaims space the other side gave
// up.
function growGroup(
  out: number[],
  indices: number[],
  amount: number,
  weight: number[],
) {
  const totalWeight = indices.reduce((sum, i) => sum + weight[i], 0);
  if (totalWeight > 0) {
    for (const i of indices) {
      out[i] += (weight[i] / totalWeight) * amount;
    }
  }
}

// Shrink the given columns of `out` by `amount` px total (amount > 0), split by
// the supplied per-column weights but floored at RESIZE_WIDTH_MIN. A column
// whose weighted share would take it below the floor is pinned to it and its
// leftover is re-split, by weight, among the columns that still have room — so
// the group can collapse toward the floor while keeping the distance-based
// shape as long as possible. `amount` is expected to be within the group's
// total capacity (the caller clamps the drag), so it is fully absorbed.
function shrinkGroup(
  out: number[],
  indices: number[],
  amount: number,
  weight: number[],
) {
  let remaining = amount;
  let active = indices.filter(
    (i) => out[i] > RESIZE_WIDTH_MIN + 1e-9 && weight[i] > 0,
  );
  while (remaining > 1e-6 && active.length > 0) {
    const totalWeight = active.reduce((sum, i) => sum + weight[i], 0);
    // Advance the distribution until either `remaining` is exhausted or the
    // first column hits the floor, whichever comes first. Distributing `s` px
    // total removes s·weight[i]/totalWeight from column i, so column i reaches
    // the floor at s = (out[i] - RESIZE_WIDTH_MIN)·totalWeight/weight[i].
    let step = remaining;
    for (const i of active) {
      step = Math.min(
        step,
        ((out[i] - RESIZE_WIDTH_MIN) * totalWeight) / weight[i],
      );
    }
    for (const i of active) {
      out[i] -= (weight[i] / totalWeight) * step;
    }
    remaining -= step;
    active = active.filter((i) => {
      if (out[i] <= RESIZE_WIDTH_MIN + 1e-9) {
        out[i] = RESIZE_WIDTH_MIN;
        return false;
      }
      return true;
    });
  }
}

// Redistribute a constant total across the columns on both sides of a dragged
// boundary. `base` holds resolved px per track; `delta` (already clamped to
// [-Σ left capacity, Σ right capacity]) widens one side of the boundary and
// narrows the other by the same amount. Dragging right (delta > 0) grows the
// columns left of the boundary and shrinks those to its right; dragging left
// does the reverse. The change is weighted by distance from the divider: the
// columns touching it move most, and each column one step further moves
// RESIZE_FALLOFF as much (the shrinking side still floors at RESIZE_WIDTH_MIN).
// Columns flagged in `fixed` get zero weight, so they keep their width
// regardless of the drag. The returned px array sums to exactly Σ(base).
export function redistribute(
  base: number[],
  leftIdx: number,
  delta: number,
  fixed: boolean[] = [],
): number[] {
  const out = base.slice();
  const leftIndices: number[] = [];
  for (let i = 0; i <= leftIdx; i++) {
    leftIndices.push(i);
  }
  const rightIndices: number[] = [];
  for (let i = leftIdx + 1; i < base.length; i++) {
    rightIndices.push(i);
  }

  // Distance-from-divider weight per column: the columns touching the boundary
  // (leftIdx and leftIdx + 1) get weight 1, falling off by RESIZE_FALLOFF for
  // each step away. Fixed columns are pinned at weight 0 so they never move.
  const weight = base.map(() => 0);
  for (const i of leftIndices) {
    weight[i] = fixed[i] ? 0 : Math.pow(RESIZE_FALLOFF, leftIdx - i);
  }
  for (const i of rightIndices) {
    weight[i] = fixed[i] ? 0 : Math.pow(RESIZE_FALLOFF, i - (leftIdx + 1));
  }

  if (delta > 0) {
    growGroup(out, leftIndices, delta, weight);
    shrinkGroup(out, rightIndices, delta, weight);
  } else if (delta < 0) {
    shrinkGroup(out, leftIndices, -delta, weight);
    growGroup(out, rightIndices, -delta, weight);
  }

  // Mop up sub-pixel float drift so the tracks sum to exactly the original
  // total; land it on the boundary column being dragged.
  const target = base.reduce((sum, w) => sum + w, 0);
  const got = out.reduce((sum, w) => sum + w, 0);
  out[leftIdx] += target - got;
  return out;
}

// Builds the grid-template-columns track list, letting tracks stretch to fill
// the container when none of them is already flexible. A flexible (fr/auto)
// track absorbs slack on its own, so in that case the widths are used as-is.
export function fillTracks(widths: string[]): string[] {
  const hasFlexTrack = widths.some(
    (w) => w.includes("fr") || w.includes("auto"),
  );
  if (hasFlexTrack) {
    return widths;
  }
  return widths.map(withFlexMax);
}

// Replaces a track's max with a flexible (fr) factor so it can grow into the
// available space, keeping its current size as the lower bound. Slack is
// distributed in proportion to a percentage min when there is one (e.g. "12%"
// -> "minmax(12%, 12fr)"); otherwise the track gets an equal share ("1fr").
function withFlexMax(width: string): string {
  const min = minmaxMin(width) ?? width;
  const pct = min.match(/^([\d.]+)%$/);
  const flex = pct ? `${pct[1]}fr` : "1fr";
  return `minmax(${min}, ${flex})`;
}

// Returns the min track of a `minmax(min, max)` value, or undefined if `width`
// is not a (well-formed) minmax. Splits on the top-level comma so commas nested
// inside min()/max()/clamp()/calc() don't cause a mis-split.
function minmaxMin(width: string): string | undefined {
  const inner = width.match(/^minmax\((.*)\)$/);
  if (!inner) {
    return undefined;
  }
  const args = inner[1];
  let depth = 0;
  for (let i = 0; i < args.length; i++) {
    const ch = args[i];
    if (ch === "(") {
      depth++;
    } else if (ch === ")") {
      depth--;
    } else if (ch === "," && depth === 0) {
      return args.slice(0, i).trim();
    }
  }
  return undefined;
}

const DefaultCellContentRenderer = function <T>({
  fieldData,
}: {
  field: keyof T;
  fieldData: any;
  rowData: T;
}) {
  return <>{fieldData == null ? "" : String(fieldData)}</>;
};

const SortCaretUp: React.FunctionComponent = () => {
  return (
    <svg
      className="inline align-middle flex-grow-0 flex-shrink-0 basis-6 h-4 w-4 fill-current"
      width="18"
      height="18"
      viewBox="0 0 24 24"
    >
      <path d="M7 14l5-5 5 5z"></path>
      <path d="M0 0h24v24H0z" fill="none"></path>
    </svg>
  );
};

const SortCaretDown: React.FunctionComponent = () => {
  return (
    <svg
      className="inline align-middle flex-grow-0 flex-shrink-0 basis-6 h-4 w-4 fill-current"
      width="18"
      height="18"
      viewBox="0 0 24 24"
    >
      <path d="M7 10l5 5 5-5z"></path>
      <path d="M0 0h24v24H0z" fill="none"></path>
    </svg>
  );
};

const HeightWrapper = ({
  children,
  isPaginated,
  page,
}: {
  isPaginated: boolean;
  page: number;
  children: React.ReactNode;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [wrapperMinHeight, setWrapperMinHeight] = useState<number | undefined>(
    undefined,
  );
  // Set the highest page's height as wrapper's min height to avoid the
  // pagination footer position move between the pages with different heights
  // (e.g. the last page of results has less data/height than others).
  useEffect(() => {
    if (wrapperRef.current) {
      const currHeight = wrapperRef.current.getBoundingClientRect().height;
      if (wrapperMinHeight === undefined || currHeight > wrapperMinHeight) {
        setWrapperMinHeight(currHeight);
      }
    }
  }, [page, wrapperMinHeight]);

  if (!isPaginated) {
    return <>{children}</>;
  }
  return (
    <div ref={wrapperRef} style={{ minHeight: wrapperMinHeight }}>
      {children}
    </div>
  );
};

export const NoDataGridContainer = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => {
  return <div className={classNames(styles.cell, className)}>{children}</div>;
};

export function sortNullsLast({
  fieldData,
}: {
  fieldData: number | null;
}): number {
  return fieldData == null ? -Infinity : fieldData;
}

function NumberCellRenderer({ fieldData }: { fieldData: number }) {
  return formatNumber(fieldData);
}

function MakePrecisionRenderer(precision: number): typeof NumberCellRenderer {
  return function PrecisionNumberCellRenderer({
    fieldData,
  }: {
    fieldData: number;
  }) {
    return formatNumber(fieldData, precision);
  };
}

export const NumberCell = Object.assign(NumberCellRenderer, {
  precision: MakePrecisionRenderer,
});

export function MsCell({ fieldData }: { fieldData: number }) {
  return formatMs(fieldData);
}

export function BytesCell({ fieldData }: { fieldData: number }) {
  return formatBytes(fieldData);
}

export default GridImpl;
